import React from 'react';
import { useStore } from '@/state/store';
import { useI18n } from '@/i18n/I18nContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { findEdgeGlobal, findDeviceGlobal } from '@/lib/detail';
import { resolvePortVlans } from '@/lib/vlanPorts';
import { edgeVlanMismatch, resolvePassthrough } from '@/lib/vlanMismatch';
import { deviceLabel, isPassiveDevice } from '@/lib/deviceIcons';
import { Cable, ArrowRightLeft, Check, AlertTriangle } from 'lucide-react';

const basePort = (id) => (typeof id === 'string' ? id.replace(/__[lr]$/, '') : id);

const VlanPreview = ({ resolved, t }) => {
  const u = resolved?.untaggedVlan || null;
  const tags = resolved?.taggedVlans || [];
  return (
    <div className="space-y-1.5" data-testid="conn-vlan-preview">
      <div className="flex items-center gap-2 text-xs">
        <span className="w-16 shrink-0 uppercase tracking-wide text-muted-foreground">{t('conn_untagged')}</span>
        {u ? (
          <span
            className="inline-flex items-center gap-1.5 rounded border px-1.5 py-0.5 font-mono text-[11px]"
            style={{ borderColor: u.color, backgroundColor: `${u.color}14`, color: '#111418' }}
          >
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: u.color }} />
            VLAN {u.vlanId} · {u.name}
          </span>
        ) : (
          <span className="text-muted-foreground">{t('conn_no_vlan')}</span>
        )}
      </div>
      <div className="flex items-start gap-2 text-xs">
        <span className="w-16 shrink-0 pt-0.5 uppercase tracking-wide text-muted-foreground">{t('conn_tagged')}</span>
        <span className="flex flex-wrap gap-1">
          {tags.length ? (
            tags.map((v) => (
              <span
                key={v.id}
                className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[11px]"
                style={{ borderColor: v.color, color: '#111418' }}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: v.color }} />
                {v.vlanId}
              </span>
            ))
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </span>
      </div>
    </div>
  );
};

const SideColumn = ({ title, dev, ports, value, onChange, resolved, usage, testid, t, passthrough, passiveEmpty }) => (
  <div className="flex-1 space-y-3 rounded-md border border-border bg-white p-3" data-testid={`${testid}-column`}>
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</span>
      <span className="ml-auto truncate text-sm font-semibold text-[#111418]" data-testid={`${testid}-device`}>{dev}</span>
    </div>
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">{t('source_port')}</Label>
      <Select value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? null : v)}>
        <SelectTrigger className="h-9" data-testid={`${testid}-port-select`}>
          <SelectValue placeholder={t('no_port')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">{t('no_port')}</SelectItem>
          {ports.map((p) => {
            const u = usage && usage[p.id];
            return (
              <SelectItem key={p.id} value={p.id} data-testid={`${testid}-port-opt-${p.id}`}>
                <span className="flex w-full items-center gap-2">
                  <span className="shrink-0 font-mono">{p.name}</span>
                  {u && (
                    <span
                      className="ml-auto flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground"
                      title={`${u.peerName} \u00b7 ${u.peerPort}`}
                    >
                      <Cable className="h-3 w-3 shrink-0 opacity-70" />
                      <span className="truncate">{u.peerName}</span>
                      <span className="shrink-0 font-mono opacity-70">{`\u00b7 ${u.peerPort}`}</span>
                    </span>
                  )}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
    {value && passthrough && (
      <p className="flex items-start gap-1.5 rounded border border-[#0B5FFF]/20 bg-[#0B5FFF]/[0.05] px-2 py-1.5 text-[11px] leading-snug text-[#0B4FBF]" data-testid={`${testid}-passthrough-note`}>
        <ArrowRightLeft className="mt-0.5 h-3 w-3 shrink-0" />
        <span>{t('conn_passthrough_note', { name: passthrough.name })}</span>
      </p>
    )}
    {value && passiveEmpty && (
      <p className="rounded border border-dashed border-border px-2 py-1.5 text-[11px] leading-snug text-muted-foreground" data-testid={`${testid}-passthrough-none`}>
        {t('conn_passthrough_none')}
      </p>
    )}
    {resolved?.groupName && (
      <p className="text-[11px] text-muted-foreground">{t('conn_group_note', { name: resolved.groupName })}</p>
    )}
    <VlanPreview resolved={value ? resolved : null} t={t} />
  </div>
);

export const ConnectionDialog = () => {
  const { t, language } = useI18n();
  const doc = useStore((s) => s.doc);
  const connDialogEdgeId = useStore((s) => s.connDialogEdgeId);
  const closeConnDialog = useStore((s) => s.closeConnDialog);
  const updateEdge = useStore((s) => s.updateEdge);
  const copyEdgeVlanConfig = useStore((s) => s.copyEdgeVlanConfig);
  const deleteEdge = useStore((s) => s.deleteEdge);

  const hit = connDialogEdgeId ? findEdgeGlobal(doc, connDialogEdgeId) : null;
  const edge = hit?.edge || null;
  const open = !!edge;

  const src = edge ? findDeviceGlobal(doc, edge.source) : null;
  const tgt = edge ? findDeviceGlobal(doc, edge.target) : null;
  const srcPorts = src?.data?.ports || [];
  const tgtPorts = tgt?.data?.ports || [];
  const nameOf = (n) => n?.data?.label || (n ? deviceLabel(n.data?.deviceType, language) : '—');

  // Show the connected counterpart for ports that are already wired elsewhere
  // (mirrors the behaviour of the connection inspector). The current edge is
  // excluded so its own port is not flagged as "in use".
  const portNameOf = (devId, portId) => {
    const n = findDeviceGlobal(doc, devId);
    return n?.data?.ports?.find((p) => p.id === portId)?.name || '\u2014';
  };
  const buildPortUsage = (devId) => {
    const map = {};
    if (!edge) return map;
    (doc.pages || []).forEach((pg) => {
      (pg.edges || []).forEach((e) => {
        if (e.id === edge.id) return;
        if (e.source === devId && e.sourcePortId && !map[e.sourcePortId]) {
          map[e.sourcePortId] = { peerName: nameOf(findDeviceGlobal(doc, e.target)), peerPort: portNameOf(e.target, e.targetPortId) };
        } else if (e.target === devId && e.targetPortId && !map[e.targetPortId]) {
          map[e.targetPortId] = { peerName: nameOf(findDeviceGlobal(doc, e.source)), peerPort: portNameOf(e.source, e.sourcePortId) };
        }
      });
    });
    return map;
  };
  const srcUsage = edge ? buildPortUsage(edge.source) : {};
  const tgtUsage = edge ? buildPortUsage(edge.target) : {};

  // Resolve the VLAN preview for each side. For a PASSIVE device (patch panel)
  // the effective config is passed through from the nearest ACTIVE device on
  // the far side; we surface that config + a hint naming the source device.
  const resolveSide = (dev, portId) => {
    if (!dev || !portId) return { resolved: null, passthrough: null, passiveEmpty: false };
    const pid = basePort(portId);
    if (!isPassiveDevice(dev.data?.deviceType)) {
      return { resolved: resolvePortVlans(dev, doc)[pid], passthrough: null, passiveEmpty: false };
    }
    const far = edge ? resolvePassthrough(doc, dev.id, pid, edge.id, new Set()) : null;
    if (!far || !far.node) return { resolved: null, passthrough: null, passiveEmpty: true };
    const farResolved = resolvePortVlans(far.node, doc)[far.portId] || null;
    const farName = far.node.data?.label || deviceLabel(far.node.data?.deviceType, language);
    const farPortName = (far.node.data?.ports || []).find((p) => p.id === far.portId)?.name || far.portId;
    return { resolved: farResolved, passthrough: { name: `${farName} \u00b7 ${farPortName}` }, passiveEmpty: false };
  };
  const srcSide = resolveSide(src, edge?.sourcePortId);
  const tgtSide = resolveSide(tgt, edge?.targetPortId);

  const bothAssigned = !!(edge && edge.sourcePortId && edge.targetPortId);
  const mismatch = edge ? edgeVlanMismatch(doc, edge, language) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) closeConnDialog(); }}>
      <DialogContent className="max-w-3xl" data-testid="connection-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cable className="h-5 w-5" /> {t('conn_dialog_title')}
          </DialogTitle>
          <DialogDescription>{t('conn_dialog_desc')}</DialogDescription>
        </DialogHeader>

        {edge && (
          <div className="space-y-4">
            <div className="flex items-stretch gap-2">
              <SideColumn
                title={t('conn_side_a')}
                dev={nameOf(src)}
                ports={srcPorts}
                value={edge.sourcePortId}
                onChange={(v) => updateEdge(edge.id, { sourcePortId: v })}
                resolved={srcSide.resolved}
                usage={srcUsage}
                testid="conn-source"
                t={t}
                passthrough={srcSide.passthrough}
                passiveEmpty={srcSide.passiveEmpty}
              />
              <div className="flex items-center justify-center px-1 text-muted-foreground">
                <ArrowRightLeft className="h-4 w-4" />
              </div>
              <SideColumn
                title={t('conn_side_b')}
                dev={nameOf(tgt)}
                ports={tgtPorts}
                value={edge.targetPortId}
                onChange={(v) => updateEdge(edge.id, { targetPortId: v })}
                resolved={tgtSide.resolved}
                usage={tgtUsage}
                testid="conn-target"
                t={t}
                passthrough={tgtSide.passthrough}
                passiveEmpty={tgtSide.passiveEmpty}
              />
            </div>

            {/* VLAN comparison + optional adopt actions */}
            {!bothAssigned ? (
              <div className="rounded-md border border-dashed border-border px-3 py-2.5 text-xs text-muted-foreground" data-testid="conn-assign-hint">
                {t('conn_assign_both_hint')}
              </div>
            ) : srcSide.passiveEmpty || tgtSide.passiveEmpty ? null : mismatch ? (
              <div className="space-y-3 rounded-md border border-[#B7791F]/40 bg-[#B7791F]/5 p-3" data-testid="conn-mismatch-banner">
                <div className="flex items-center gap-2 text-sm font-medium text-[#8a5a12]">
                  <AlertTriangle className="h-4 w-4" /> {t('conn_vlan_mismatch')}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => copyEdgeVlanConfig(edge.id, 'a')} data-testid="conn-adopt-a">
                    {t('conn_adopt_a')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => copyEdgeVlanConfig(edge.id, 'b')} data-testid="conn-adopt-b">
                    {t('conn_adopt_b')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={closeConnDialog} data-testid="conn-keep">
                    {t('conn_keep')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-md border border-[#0F7B6C]/40 bg-[#0F7B6C]/5 px-3 py-2.5 text-sm text-[#0F7B6C]" data-testid="conn-match-banner">
                <Check className="h-4 w-4" /> {t('conn_vlan_match')}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <Button
            variant="ghost"
            className="text-[#B42318] hover:bg-[#B42318]/5 hover:text-[#B42318]"
            onClick={() => edge && deleteEdge(edge.id)}
            data-testid="conn-delete"
          >
            {t('delete_connection')}
          </Button>
          <Button onClick={closeConnDialog} data-testid="conn-done">
            {t('conn_done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
